import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert } from "@mui/material";
import Label from "../../../Utility/Label/Label";
import Button from "../../../Utility/Button/Button";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import ErrorMsg from "../../../Utility/ErrorMsg";
import CommonModal from "../../../Utility/CommonModel/CommonModal";
import { EditTeamMemberSchema } from "../../../FormsValidations/Validations";
import Input from "../../../Utility/Input/Input";
import { updateTeamMember } from "../../../Redux/Thunks/teamsThunk";
import { fetchUserRoles } from "../../../Redux/Thunks/usersThunk";
import { getListFromApiResponse } from "../teamMappers";

const EditTeamMemberModal = ({ isOpen, onClose, member, teamId, onSuccess, onError }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [roleList, setRoleList] = useState([]);

  const roleOptions = useMemo(
    () =>
      roleList.map((r) => ({
        value: String(r.id),
        label: r.name,
      })),
    [roleList]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(EditTeamMemberSchema),
    defaultValues: {
      role: "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchUserRoles())
      .unwrap()
      .then((raw) => setRoleList(getListFromApiResponse(raw)))
      .catch(() => setRoleList([]));
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!member) return;
    const rid = member.roleId ?? member.role_id;
    const byName = roleList.find((r) => r.name === member.role)?.id;
    const value = rid ?? byName ?? "";
    reset({ role: value !== "" && value != null ? String(value) : "" });
  }, [member, reset, roleList]);

  const onSubmit = async (data) => {
    if (!teamId || !member?.id) {
      setErrorMsg("Missing team or member.");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    try {
      await dispatch(
        updateTeamMember({
          teamId,
          userId: member.id,
          payload: { roleId: data.role },
        })
      ).unwrap();
      const picked = roleList.find((r) => String(r.id) === String(data.role));
      if (onSuccess)
        onSuccess({
          id: member.id,
          role: picked?.name ?? member.role,
          roleId: picked?.id ?? data.role,
        });
      onClose();
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        "Failed to update member. Please try again.";
      setErrorMsg(msg);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setErrorMsg("");
      onClose();
    }
  };

  if (!isOpen || !member) return null;

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Member"
      width="max-w-md"
      loading={loading}
    >
      {errorMsg && (
        <Alert className="mb-4" severity="error">
          {errorMsg}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <Label htmlFor="edit-member-email">Email Address</Label>
          <Input
            id="edit-member-email"
            type="email"
            placeholder="Enter email address"
            value={member.email ?? ""}
            noWrapper={true}
            className="w-full text-sm border border-border-primary rounded-sm px-3 py-1.5 text-text-secondary bg-surface-secondary cursor-not-allowed"
            readOnly
          />
        </div>

        <div className="mb-4">
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                label="Role"
                options={roleOptions}
                placeholder="Select role"
                error={errors.role?.message}
                disabled={loading || roleOptions.length === 0}
                required={true}
                searchable={false}
                className="text-sm"
              />
            )}
          />
          <ErrorMsg message={errors.role?.message} className="mt-1" />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            type="button"
            variant="outline"
            size="md"
            disabled={loading}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <FormSubmitButton
            loading={loading}
            label="Save Changes"
            loadingText="Saving..."
            idleIconClass=""
            size="md"
            disabled={roleOptions.length === 0}
          />
        </div>
      </form>
    </CommonModal>
  );
};

export default EditTeamMemberModal;
